/**
 * Converts and compresses an image Blob to JPEG.
 * Always converts to JPEG regardless of input format (handles HEIC from iPhones).
 * Uses canvas to resize and re-encode.
 *
 * @param blob - The original image blob (any format including HEIC)
 * @param maxSizeBytes - Maximum size in bytes
 * @param maxDimension - Maximum width or height in pixels
 * @returns A JPEG Blob
 */
async function compressToJpeg(
  blob: Blob,
  maxSizeBytes: number,
  maxDimension: number
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(blob);

    img.onload = () => {
      URL.revokeObjectURL(url);

      let { width, height } = img;

      // Resize if exceeds max dimension
      if (width > maxDimension || height > maxDimension) {
        if (width > height) {
          height = Math.round((height * maxDimension) / width);
          width = maxDimension;
        } else {
          width = Math.round((width * maxDimension) / height);
          height = maxDimension;
        }
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      if (!ctx) return reject(new Error('Could not get canvas context'));
      ctx.drawImage(img, 0, 0, width, height);

      // Try progressively lower quality until under size limit
      let quality = 0.85;
      const tryCompress = () => {
        canvas.toBlob(
          (compressed) => {
            if (!compressed) return reject(new Error('Canvas compression failed'));
            if (compressed.size <= maxSizeBytes || quality <= 0.2) {
              resolve(compressed);
            } else {
              quality -= 0.1;
              tryCompress();
            }
          },
          'image/jpeg',
          quality
        );
      };

      tryCompress();
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image — format may not be supported'));
    };

    img.src = url;
  });
}

/**
 * Compresses an image for upload to S3.
 * Always converts to JPEG (fixes HEIC from iPhones).
 * Target: under 8MB, max 2048px — good balance for upload speed on mobile.
 */
export async function compressImageForUpload(
  blob: Blob,
  maxSizeBytes = 8 * 1024 * 1024,
  maxDimension = 2048
): Promise<Blob> {
  // Always run through canvas to ensure JPEG conversion,
  // even if the file is already small (HEIC files can be small but wrong format)
  const isHeic = blob.type === 'image/heic' || blob.type === 'image/heif';
  const isAlreadyJpeg = blob.type === 'image/jpeg' || blob.type === 'image/jpg';

  if (isAlreadyJpeg && blob.size <= maxSizeBytes) {
    // Already JPEG and small enough — still resize if too large dimensionally
    // by running through canvas anyway (canvas handles the dimension check)
    return compressToJpeg(blob, maxSizeBytes, maxDimension);
  }

  console.log(`[Compress] Input: ${blob.type || 'unknown'}, ${(blob.size / 1024 / 1024).toFixed(2)} MB`);
  const result = await compressToJpeg(blob, maxSizeBytes, maxDimension);
  console.log(`[Compress] Output: image/jpeg, ${(result.size / 1024 / 1024).toFixed(2)} MB`);
  return result;
}

/**
 * Compresses an image for the Anthropic AI API.
 * Always converts to JPEG. Target: under 4MB, max 1568px (Anthropic's recommended max).
 */
export async function compressImageForAI(
  blob: Blob,
  maxSizeBytes = 4 * 1024 * 1024,
  maxDimension = 1568
): Promise<Blob> {
  console.log(`[CompressAI] Input: ${blob.type || 'unknown'}, ${(blob.size / 1024 / 1024).toFixed(2)} MB`);
  const result = await compressToJpeg(blob, maxSizeBytes, maxDimension);
  console.log(`[CompressAI] Output: image/jpeg, ${(result.size / 1024 / 1024).toFixed(2)} MB`);
  return result;
}

/**
 * Compresses an image for VIN scanning specifically.
 * Uses a higher resolution ceiling than compressImageForAI (which targets
 * Anthropic's no-added-value cutoff for whole-image analysis) because the
 * server crops into a small sub-region of this image to read individual
 * characters — more source pixels there directly means a sharper crop.
 */
export async function compressImageForVINScan(
  blob: Blob,
  maxSizeBytes = 6 * 1024 * 1024,
  maxDimension = 2400
): Promise<Blob> {
  console.log(`[CompressVIN] Input: ${blob.type || 'unknown'}, ${(blob.size / 1024 / 1024).toFixed(2)} MB`);
  const result = await compressToJpeg(blob, maxSizeBytes, maxDimension);
  console.log(`[CompressVIN] Output: image/jpeg, ${(result.size / 1024 / 1024).toFixed(2)} MB`);
  return result;
}